import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Fingerprint } from 'lucide-react';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { Modal, Btn, Alert } from '../components/UI';
import { authApi, companyApi, passkeyErrorMessage } from '../services/api';
import { isStale } from '../utils/appVersion';

/* Friss hitelesítés (step-up) az utalás-indítás elé — 2026-08-04, F-1c.
 * Háttér: wiki/SECURITY_INCIDENT_2026-08-04.md.
 *
 * A kapu a backendben van (`require_step_up`, 403 + `X-Step-Up-Required`), ez itt
 * csak a felhasználói oldal. SZÁNDÉKOSAN ELŐZETESEN kérdezünk (`requireStepUp()`
 * a művelet ELŐTT), nem a 403-ra reagálva: egy köteges utalásnál a 403 azt
 * jelentené, hogy a tételek egy része már elment, a többi nem — a részlegesen
 * végrehajtott utalás-köteg pedig sokkal rosszabb, mint egy extra jelszó-kérés.
 * A 403-as ág ettől függetlenül él a backendben, második védvonalként.
 *
 * ⚠ S3 / audit F4 (2026-09-24, tulajdonosi döntés): step-up CSAK passkey-jel. A
 * jelszavas ág KIVEZETVE — a backend `POST /auth/step-up` 403 `passkey_kell`-t ad,
 * és a `require_step_up` csak passkey-ből született (v4, `method=passkey`) jegyet
 * fogad el. Akinek nincs passkeye, annak az ablak a regisztráció helyét mutatja
 * (Beállítások → Biztonság, `/settings/security`, „Passkey-ek" kártya).
 *
 * ⚠⚠ F6/A03 (2026-09-24) — a tulajdonosi szabály: MINDEN utalásnál külön, friss
 * passkey-azonosítás kell; nincs 30 perces ablak, nincs újrahasznosítható utalási jegy.
 * Ezért az utalás a `requirePaymentStepUp(why, requests)`-t hívja, NEM a
 * `requireStepUp`-ot: ez SOHA nem kérdezi le a státuszt és nem használ korábbi jegyet,
 * mindig ceremóniát futtat, és a ceremónia ELŐTT elküldi a konkrét kéréseket
 * (`[{endpoint, body}]`). A backend ezekre — és csak ezekre, egyszer — ad 5 perces jegyet
 * (`core/payment_step_up.py`).
 *
 * ⚠⚠ 2026-09-24, élő hiba: utalás után 3 perccel egy munkavállalói bankszámla-módosítás
 * passkey NÉLKÜL ment át (az utalási verify a 30 perces jegyet is kiírta, a felület a
 * `stepUpStatus`-ra hagyatkozott). Ezért a pénz ÚTJÁT módosító műveletek (munkavállalói és
 * szállítói bankszámla, draft-védelem KI, banki / kapcsolat-hitelesítő adat írása-törlése) a
 * `requireFreshStepUp(why, requests)`-t hívják: ugyanaz a mechanizmus, mint az utalásé — MINDIG
 * ceremónia, soha nem néz státuszt, a kérés-leíró a `services/api.js` `freshRequest`-jéből jön,
 * és a hívó PONTOSAN azt küldi el (`sendFresh`). A `requireStepUp` (30 perces jegy) már csak a
 * pénz útját nem érintő műveleteké (NAV ÜPO credential + aktiválás).
 */
const StepUpContext = createContext(null);

const passkeySupported = browserSupportsWebAuthn();

export function StepUpProvider({ children }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  // A böngésző `NotAllowedError`-ja két esetet takar, amit NEM tudunk szétválasztani:
  // a felhasználó megszakította a ceremóniát, VAGY ezen az eszközön nincs passkeye.
  // Ezért nem hiba (piros), hanem tájékoztatás a regisztráció helyével.
  const [cancelHint, setCancelHint] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const resolveRef = useRef(null);
  // Az ÉPPEN futó utalási ceremónia szándéka (`{requests}`) — null = nem utalási step-up.
  const intentRef = useRef(null);
  const [paymentCount, setPaymentCount] = useState(0);
  // 'payment' | 'action' | null — csak a modal szövegéhez (a mechanizmus ugyanaz).
  const [intentKind, setIntentKind] = useState(null);
  // M1 (2026-09-04): amíg egy utalás-köteg fut, a cég-váltó gombot le kell
  // tiltani (Layout.js). Ez az EGYETLEN boolean egy már létező contexten — a
  // `Documents.js` `handlePayAll` ugyanott állítja, ahol az `isPaying`-et
  // (szinkron, a kattintás első sorában, `.claude/rules/utalas-biztonsag.md` 2.d).
  const [paymentInFlight, setPaymentInFlight] = useState(false);
  // A cégnév a pénzt mozgató megerősítéseken (D1 UX §0: ez MA NEM létezett).
  // Ugyanaz a `['company']` cache-bejegyzés, amit a Layout is használ — nincs
  // extra hálózati hívás, és nem torzulhat el három különböző forrásból.
  const { data: company } = useQuery({
    queryKey: ['company'], queryFn: companyApi.get, staleTime: 5 * 60 * 1000,
  });
  const companyName = company?.name || '';

  const finish = useCallback((ok) => {
    setOpen(false);
    setError(null);
    setCancelHint(false);
    const resolve = resolveRef.current;
    resolveRef.current = null;
    intentRef.current = null;
    setPaymentCount(0);
    setIntentKind(null);
    resolve?.(ok);
  }, []);

  // A passkey-regisztráció helye (pages/misc.js → Settings, „Biztonság" csoport).
  // A megkezdett műveletet megszakítjuk (false) — regisztráció után újra kell indítani.
  const goRegister = () => {
    finish(false);
    navigate('/settings/security');
  };

  /** Utalás előtt: MINDIG friss ceremónia, a konkrét kérésekre kötve (F6/A03).
   *  `requests`: `[{endpoint, body}]` — pontosan azok az objektumok, amiket a hívó
   *  utána elküld. true = mehet (a jegy 5 percig, kérésenként egyszer jó). */
  const startIntent = useCallback(async (kind, why, requests) => {
    if (!Array.isArray(requests) || requests.length === 0) return false;
    // Egyszerre egy ceremónia: egy függő másik ígéretet nem írunk felül csendben.
    if (resolveRef.current) return false;
    // 2026-09-24: régi bundle-lel (deploy előtt megnyitott lap) nem indítunk érzékeny
    // műveletet — az `isStale` találatkor a „Új verzió érhető el" sávot is megjeleníti.
    if (await isStale()) return false;
    if (resolveRef.current) return false;
    intentRef.current = { requests };
    setPaymentCount(requests.length);
    setIntentKind(kind);
    setReason(why || '');
    setOpen(true);
    return new Promise((resolve) => { resolveRef.current = resolve; });
  }, []);
  const requirePaymentStepUp = useCallback(
    (why, requests) => startIntent('payment', why, requests), [startIntent]);

  /** A pénz ÚTJÁT módosító művelet előtt: MINDIG friss ceremónia, a konkrét kérés(ek)re
   *  kötve (`freshRequest(...)` leírók). true = mehet; a jegy 5 percig, kérésenként egyszer jó. */
  const requireFreshStepUp = useCallback(
    (why, requests) => startIntent('action', why, requests), [startIntent]);

  /** true, ha mehet a művelet; false, ha a felhasználó megszakította.
   *  ⚠ NEM utalásra és NEM a pénz útját módosító műveletre — ott a `requirePaymentStepUp` /
   *  `requireFreshStepUp` kell (a backend a 30 perces jegyet ott nem fogadja el). */
  const requireStepUp = useCallback(async (why = '') => {
    try {
      const status = await authApi.stepUpStatus();
      if (status?.valid) return true;
    } catch {
      // 401 esetén az axios-interceptor már intézi a frissítést/átirányítást;
      // itt a biztonságos irány a megerősítés-kérés.
    }
    setReason(why);
    setOpen(true);
    return new Promise((resolve) => { resolveRef.current = resolve; });
  }, []);

  // Passkey-ág (wiki/proposals/deferred-features.md §15): egy friss
  // navigator.credentials.get() ceremónia — 2026-09-24 óta az EGYETLEN step-up út.
  const submitPasskey = async () => {
    setError(null);
    setCancelHint(false);
    setPasskeyBusy(true);
    try {
      const intent = intentRef.current;
      const { challenge_id: challengeId, options } = await authApi.stepUpPasskeyOptions(intent);
      const credential = await startAuthentication({ optionsJSON: options });
      await authApi.stepUpPasskeyVerify(challengeId, credential, !!intent);
      finish(true);
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        setCancelHint(true);
      } else {
        // A backend 403 `passkey_kell` (strukturált detail) is ide eshet — a saját
        // üzenetét mutatjuk; a regisztráció linkje lent mindig látszik.
        const d = err?.response?.data?.detail;
        setError(d && typeof d === 'object' && d.error === 'passkey_kell' && d.message
          ? d.message
          : passkeyErrorMessage(err, 'A passkey-megerősítés sikertelen.'));
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  return (
    <StepUpContext.Provider value={{ requireStepUp, requirePaymentStepUp, requireFreshStepUp,
                                     stepUpOpen: open,
                                     paymentInFlight, setPaymentInFlight }}>
      {children}
      <Modal open={open} onClose={() => finish(false)} width={420}
        title={<span style={{display:'flex',alignItems:'center',gap:8}}>
          <ShieldCheck size={16}/> Biztonsági megerősítés
        </span>}>
        <div style={{display:'flex',flexDirection:'column',gap:'0.8rem'}}>
          {error && <Alert type="danger">{error}</Alert>}
          {cancelHint && !error && (
            <Alert type="info">
              A megerősítés nem történt meg. Ha ezen az eszközön még nincs passkeyd, előbb
              regisztrálj egyet (lent a link), utána indítsd újra a műveletet.
            </Alert>
          )}
          {companyName && (
            <div style={{display:'flex',alignItems:'baseline',gap:8}}>
              <span style={{fontSize:'0.68rem',fontWeight:600,color:'var(--text-muted)',
                textTransform:'uppercase',letterSpacing:'0.06em'}}>Cég</span>
              <span style={{fontSize:'0.88rem',fontWeight:700,color:'var(--text)'}}>{companyName}</span>
            </div>
          )}
          <p style={{fontSize:'0.8rem',color:'var(--text-muted)',lineHeight:1.5,margin:0}}>
            {reason || 'Pénzügyi művelet indítása előtt'} passkey-jel megerősítjük, hogy tényleg te
            ülsz a gép előtt.{' '}
            {intentKind === 'payment'
              ? `A megerősítés csak erre az utalás-indításra érvényes (${paymentCount} banki
                 kérés), mindegyikre egyszer, 5 percig — minden új utalás új megerősítést kér.`
              : intentKind === 'action'
                ? `A megerősítés csak ${paymentCount > 1 ? `erre a ${paymentCount} módosításra` : 'erre az egy módosításra'} érvényes, mindegyikre egyszer, 5 percig — minden új módosítás (bankszámla, Revolut-partner, banki hozzáférés, draft-védelem) új megerősítést kér.`
                : 'A megerősítés 30 percig érvényes; utalás jelszóval nem indítható, és utaláshoz mindig külön megerősítés kell.'}
          </p>

          {passkeySupported ? (
            <Btn type="button" loading={passkeyBusy} onClick={submitPasskey}
              style={{ justifyContent: 'center', padding: '0.7rem 0.9rem', fontSize: '0.92rem' }}>
              <Fingerprint size={20} style={{ marginRight: 8 }} /> Megerősítés passkey-jel
            </Btn>
          ) : (
            <Alert type="warning">
              Ez a böngésző nem támogatja a passkey-t, ezért innen nem indítható utalás.
              Használj passkey-képes böngészőt vagy eszközt (Windows Hello, Touch ID, telefon,
              biztonsági kulcs).
            </Alert>
          )}

          <p style={{fontSize:'0.76rem',color:'var(--text-muted)',lineHeight:1.5,margin:0}}>
            Még nincs passkeyd? A{' '}
            <button type="button" onClick={goRegister}
              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent)',
                cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline' }}>
              Beállítások → Biztonság → Passkey-ek
            </button>{' '}
            résznél regisztrálhatsz egyet.
          </p>

          <div style={{display:'flex',justifyContent:'flex-end'}}>
            <Btn type="button" variant="ghost" onClick={() => finish(false)}>Mégse</Btn>
          </div>
        </div>
      </Modal>
    </StepUpContext.Provider>
  );
}

export function useStepUp() {
  const ctx = useContext(StepUpContext);
  if (!ctx) throw new Error('useStepUp csak StepUpProvideren belül használható');
  return ctx;
}
